import { useEffect, useRef, useState, useMemo } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";

const STEPS = [
  { n: 1, label: "Project Setup", desc: "Name the project, pick the target commodity and the client." },
  { n: 2, label: "Area of Interest", desc: "Upload the licence boundary (KML/KMZ) that defines the survey area." },
  { n: 3, label: "Reference & Geology", desc: "Add any reference geology or field data to guide interpretation." },
  { n: 4, label: "Sensor & Bands", desc: "Choose sensors and the spectral band ratios that drive prospectivity." },
  { n: 5, label: "Imagery & Prospectivity", desc: "Acquire satellite imagery and fuse the evidence into a prospectivity model." },
  { n: 6, label: "Target Review", desc: "Review the ranked exploration targets and data confidence before reporting." },
  { n: 7, label: "Reports & Delivery", desc: "Generate the 18-map suite, technical reports and client downloads." },
];

export function StepBar({ step, maxStep, onGo, pid }) {
  const pct = Math.min(100, Math.max(0, Math.round(((maxStep - 1) / (STEPS.length - 1)) * 100)));
  const [celebrate, setCelebrate] = useState(false);
  const firedRef = useRef(false);
  const confetti = useMemo(() => Array.from({ length: 18 }).map((_, i) => ({
    left: (i / 18) * 100, delay: (i % 6) * 0.05,
    color: ["#C20511", "#E5C06B", "#F4E3B0", "#FFFFFF"][i % 4],
    dx: (Math.random() * 2 - 1) * 46,
  })), []);
  useEffect(() => {
    if (pct < 100 || firedRef.current) return;
    firedRef.current = true;
    const key = `symin-setup-done-${pid || "x"}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    setCelebrate(true);
    const t = setTimeout(() => setCelebrate(false), 3400);
    toast.success("Setup complete", { description: "All setup steps are done - this project is fully processed and ready to report." });
    return () => clearTimeout(t);
  }, [pct, pid]);
  return (
    <div className="relative overflow-hidden w-full border-b border-border bg-card/90 backdrop-blur-md sticky top-[52px] z-[800]" data-testid="wizard-stepbar">
      {celebrate && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-[810] h-0" data-testid="wizard-confetti" aria-hidden="true">
          <style>{`@keyframes symin-confetti{0%{opacity:0;transform:translateY(-8px) rotate(0)}12%{opacity:1}100%{opacity:0;transform:translateY(70px) translateX(var(--dx,0)) rotate(340deg)}}`}</style>
          {confetti.map((c, i) => (
            <span key={i} style={{ position: "absolute", top: 0, left: `${c.left}%`, width: 7, height: 11, borderRadius: 2, background: c.color, "--dx": `${c.dx}px`, animation: `symin-confetti 1.5s ease-out ${c.delay}s forwards` }} />
          ))}
        </div>
      )}
      <div className="h-1 w-full bg-border/50" data-testid="wizard-progress-track">
        <div className={`h-full bg-primary transition-all duration-500 ease-out ${celebrate ? "animate-pulse" : ""}`}
          style={{ width: `${pct}%`, boxShadow: celebrate ? "0 0 12px 2px rgba(201,154,60,0.9)" : "none" }} data-testid="wizard-progress-fill" />
      </div>
      <div className="max-w-[1760px] mx-auto px-6 py-4 flex items-center gap-2 sm:gap-4 overflow-x-auto">
        {STEPS.map((s, i) => {
          const active = s.n === step;
          const done = s.n < step;
          const reachable = s.n <= maxStep;
          return (
            <div key={s.n} className="flex items-center gap-2 sm:gap-4 shrink-0">
              <button
                data-testid={`step-nav-${s.n}`}
                aria-label={`Step ${s.n}: ${s.label}`}
                disabled={!reachable}
                onClick={() => reachable && onGo(s.n)}
                className={`flex items-center gap-3 group ${reachable ? "cursor-pointer" : "cursor-not-allowed opacity-40"}`}
              >
                <span
                  className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors
                    ${active
                      ? "bg-primary text-primary-foreground shadow-[0_0_0_4px_hsl(var(--primary)/0.18)]"
                      : done
                        ? "bg-primary/15 text-primary border border-primary/40"
                        : "bg-muted text-muted-foreground"}`}
                >
                  {done && !active ? <Check size={16} /> : String(s.n).padStart(2, "0")}
                </span>
                <span className={`hidden sm:block text-sm font-semibold tracking-tight ${active ? "text-foreground" : done ? "text-foreground/80" : "text-muted-foreground"}`}>
                  {s.label}
                </span>
              </button>
              {i < STEPS.length - 1 && <div className={`h-px w-4 sm:w-8 ${s.n < step ? "bg-primary/50" : "bg-border"}`} />}
            </div>
          );
        })}
      </div>
      <div className="max-w-[1760px] mx-auto px-6 pb-3 -mt-1.5 flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground" data-testid="wizard-step-caption">{STEPS.find((s) => s.n === step)?.desc || ""}</p>
        <span className="text-[11px] font-mono font-semibold whitespace-nowrap shrink-0" style={{ color: "#C20511" }} data-testid="wizard-progress-label">{pct}% complete</span>
      </div>
    </div>
  );
}
